import {
  Button,
  ConfigProvider,
  Empty,
  Layout,
  message,
  Table,
  Tooltip,
  Typography,
} from "antd";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineCopy, AiOutlineLeft, AiOutlineRight } from "react-icons/ai";

import { useScreenSize } from "design-system/hooks";
import { useQueryParams } from "shell/api/hooks";
import { copyToClipboard, stopPropagation } from "shell/api/utils";

import type { DataTableProps, EnhancedColumnType } from "./types";

import { ColumnsOrderModal } from "./ColumnsOrderModal";
import s from "./DataTable.module.css";
import { FiltersMenu } from "./FiltersMenu";
import { Header } from "./Header";
import { TabFilters } from "./TabFilters";

const { Text } = Typography;

const expandedRowRender = (
  record: any,
  expandable: DataTableProps["expandable"],
) => {
  if (expandable?.customRender) {
    return expandable.customRender(record);
  }

  if (!expandable?.dataAccessor) {
    throw new Error(
      "DataTable - `expandable.dataAccessor` is required when using nested table columns.",
    );
  }

  const nestedData = expandable.dataAccessor(record);

  return (
    <Table
      columns={expandable.columns}
      dataSource={nestedData}
      pagination={false}
      size="small"
      rowKey={expandable.rowKey ?? "id"}
    />
  );
};

const handleBandedRows: DataTableProps["rowClassName"] = (_, index) => {
  if (!index) {
    return "";
  }

  return index % 2 === 1 ? s.evenRow : "";
};

const DataTable = ({
  addNewAction,
  searchInput,
  style,
  testId,
  viewMode,
  setViewMode,
  className,
  rightActions,
  tabFilters,
  data,
  columns,
  loading,
  pagination = false,
  onRowClick,
  rowStyle,
  rowKey,
  copyable,
  rowClassName,
  scroll,
  showHeader,
  size,
  rowSelection,
  expandable,
  customizableColumns,
  tableProps,
  withUpdateUrl,
  filters,
  title,
  bandedRows = false,
  renderEmpty,
}: DataTableProps) => {
  const screenSize = useScreenSize();
  const { t } = useTranslation(["common"]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const { updateQuery, resetQuery, query } = useQueryParams();

  const [currentPage, setCurrentPage] = useState(
    withUpdateUrl ? +(query?.page ?? 1) : 1,
  );
  const [pageSize, setPageSize] = useState(
    withUpdateUrl ? +(query?.pageSize ?? 10) : 10,
  );

  // Pagination state for cursor based pagination
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);

  const isClientPagination = pagination === true;
  const isServerPagination =
    typeof pagination === "object" && "lastEvaluatedKey" in pagination;
  const total = data?.length;

  const getDefaultRenderEmpty = useCallback(
    () => (
      <div style={{ textAlign: "center" }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={false}
          className={s.noRelatedImage}
        />
        <Text className={s.noRelatedResult}>{t("labels.noRelatedResult")}</Text>
      </div>
    ),
    [t],
  );

  const toggleOrderModal = () => {
    setIsOrderModalOpen(prev => !prev);
  };

  const updateColumnsOrder = (updatedColumns: EnhancedColumnType<any>[]) => {
    if (customizableColumns?.tableName) {
      localStorage.setItem(
        customizableColumns?.tableName,
        JSON.stringify(updatedColumns),
      );

      customizableColumns?.callback?.();
    }
  };

  const renderServerPaginationControls = () => {
    if (!isServerPagination || !pagination) return null;

    const hasNext =
      "hasNextPage" in pagination
        ? pagination.hasNextPage
        : !!pagination.lastEvaluatedKey;

    const handleNext = () => {
      if (!hasNext) return;

      setCursorStack(prev => [...prev, currentCursor]);
      setCurrentCursor(pagination.lastEvaluatedKey ?? null);
      pagination.onNext(pagination.lastEvaluatedKey as string);
    };

    const handlePrev = () => {
      if (!cursorStack.length) return;

      const prevStack = [...cursorStack];
      const prevCursor = prevStack.pop() ?? null;

      setCursorStack(prevStack);
      setCurrentCursor(prevCursor);
      pagination.onPrev?.(prevCursor);
    };

    return (
      <div className={s.paginationControls}>
        <Button
          type="default"
          icon={<AiOutlineLeft />}
          onClick={handlePrev}
          disabled={cursorStack.length === 0}
          data-testid="prev-button"
        />
        <Button
          type="default"
          icon={<AiOutlineRight />}
          onClick={handleNext}
          disabled={!hasNext}
          data-testid="next-button"
        />
      </div>
    );
  };

  const deps =
    typeof pagination === "object" && "resetPaginationKey" in pagination
      ? pagination.resetPaginationKey
      : null;

  useEffect(() => {
    if (isServerPagination) {
      setCursorStack([]);
      setCurrentCursor(null);
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [deps]);

  const paginatedData = useMemo(() => {
    if (!isClientPagination) return data;

    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, currentPage, pageSize, isClientPagination]);

  const normalizedPagination = useMemo(() => {
    if (
      pagination === false ||
      (typeof pagination === "object" && "lastEvaluatedKey" in pagination)
    )
      return false;

    if (pagination === true) {
      return {
        current: currentPage,
        pageSize,
        total,
        onChange: (page: number, size: number) => {
          setCurrentPage(page);
          setPageSize(size);
          if (withUpdateUrl) {
            updateQuery({ page: page.toString(), pageSize: size.toString() });
          }
        },
        showTotal: (total: number) =>
          `${t("labels.total")} ${total} ${t("labels.items")}`,
        showSizeChanger: true,
      };
    }

    return {
      ...pagination,
      onChange: (page: number, size: number) => {
        if ("onChange" in pagination && pagination) {
          pagination.onChange?.(page, size);
          if (withUpdateUrl) {
            updateQuery({ page: page.toString(), pageSize: size.toString() });
          }
        }
      },
    };
    // oxlint-disable-next-line react/exhaustive-deps
  }, [pagination, currentPage, pageSize, total]);

  const _expandable = useMemo(() => {
    if (!expandable?.customRender && !expandable?.columns) return;

    return {
      expandedRowRender: (record: any) => expandedRowRender(record, expandable),
      rowExpandable: expandable.rowExpandable,
      expandedRowClassName:
        expandable.expandedRowClassName ?? s.expandedRowClassName,
      expandIcon: expandable.expandIcon,
    };
  }, [expandable]);

  const enhancedColumns = useMemo(() => {
    const tableName = customizableColumns?.tableName;
    const storedCols: Record<string, { order: number; visible: boolean }> = {};

    if (tableName) {
      try {
        const raw = localStorage.getItem(tableName);
        const parsed = raw ? JSON.parse(raw) : [];
        parsed.forEach(
          (col: {
            dataIndex: string | string[];
            order: number;
            visible: boolean;
          }) => {
            const key = Array.isArray(col.dataIndex)
              ? col.dataIndex.join(".")
              : col.dataIndex;
            if (key) {
              storedCols[key] = {
                order: col.order,
                visible: col.visible,
              };
            }
          },
        );
      } catch (err) {
        console.warn("Invalid column config in localStorage", err);
      }
    }

    const enhanced = columns.map((col, index) => {
      if (col.orderable === false)
        return {
          ...col,
          visible: true,
          id:
            col.id ??
            (Array.isArray(col.dataIndex)
              ? col.dataIndex.join(".")
              : (col.dataIndex ?? `col-${index}`)),
        };

      const dataIndexKey = Array.isArray(col.dataIndex)
        ? col.dataIndex.join(".")
        : (col.dataIndex ?? `col-${index}`);
      const stored = storedCols[dataIndexKey];

      return {
        ...col,
        order: stored?.order ?? index,
        visible: stored?.visible ?? col?.visible ?? true,
        id: col.id ?? dataIndexKey,
      };
    });

    const orderedCols: EnhancedColumnType<any>[] = [];
    const sortableCols = enhanced.filter(c => c.orderable !== false);
    const sorted = sortableCols.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    let sortIndex = 0;
    for (const col of enhanced) {
      if (col.orderable === false) {
        orderedCols.push(col);
      } else {
        orderedCols.push(sorted[sortIndex++]);
      }
    }

    if (!copyable) return orderedCols;

    return orderedCols.map(col => {
      if (
        typeof col.render === "function" ||
        (typeof col.copyable === "boolean" && !col.copyable)
      ) {
        return col;
      }

      return {
        ...col,
        render: (value: any, record: any, index: number) => {
          const getValueByPath = (record: any, path?: string | string[]) => {
            if (!path) return;
            if (typeof path === "string") return record[path];
            return path.reduce((acc, key) => acc?.[key], record);
          };

          const text = col.dataIndex
            ? getValueByPath(record, col.dataIndex)
            : value;

          return (
            <div className={s.copyableCell}>
              <span>{text}</span>
              <Tooltip title={t("labels.copy")}>
                <AiOutlineCopy
                  data-testid={`copy-icon-${col?.dataIndex ?? value}-${index}`}
                  className={s.copyIcon}
                  onClick={e => {
                    stopPropagation(e);
                    copyToClipboard(String(text), () => {
                      message.success(t("labels.copyToClipboardSuccess"));
                    });
                  }}
                />
              </Tooltip>
            </div>
          );
        },
      };
    });
    // oxlint-disable-next-line react/exhaustive-deps
  }, [columns, copyable, customizableColumns?.tableName]);

  const memoizedTable = useMemo(
    () => (
      <ConfigProvider renderEmpty={renderEmpty ?? getDefaultRenderEmpty}>
        <Table
          rowKey={rowKey ?? "id"}
          classNames={{
            pagination: s.paginationWrapper,
            empty: s.tableEmpty,
          }}
          columns={
            customizableColumns
              ? enhancedColumns.filter(c => c.visible)
              : enhancedColumns
          }
          dataSource={paginatedData}
          loading={loading}
          pagination={normalizedPagination}
          onRow={record => ({
            onClick: () => {
              onRowClick?.(record);
            },
            style: {
              cursor: "pointer",
              ...(typeof rowStyle === "function" ? rowStyle(record) : rowStyle),
            },
          })}
          scroll={scroll}
          showHeader={showHeader}
          size={
            (size ?? ["xs", "sm"].includes(screenSize)) ? "small" : "middle"
          }
          rowClassName={(record, index) => {
            if (typeof rowClassName === "function") {
              const customClass = rowClassName?.(record, index);
              if (customClass) return customClass;
            }
            if (bandedRows) return handleBandedRows(record, index);
            return "";
          }}
          rowSelection={rowSelection}
          expandable={_expandable}
          {...tableProps}
        />
      </ConfigProvider>
    ),
    // oxlint-disable-next-line react/exhaustive-deps
    [
      enhancedColumns,
      paginatedData,
      loading,
      normalizedPagination,
      onRowClick,
      rowStyle,
      rowKey,
      scroll,
      showHeader,
      size,
      screenSize,
      rowClassName,
      rowSelection,
      _expandable,
      customizableColumns,
      title,
      tableProps,
      renderEmpty,
      getDefaultRenderEmpty,
    ],
  );

  return (
    <Layout
      className={clsx(s.container, className)}
      style={style}
      data-testid={testId}
    >
      {(addNewAction ||
        searchInput ||
        rightActions ||
        viewMode ||
        customizableColumns) && (
        <Header
          addNewAction={addNewAction}
          searchInput={searchInput}
          title={title}
          viewMode={viewMode}
          setViewMode={setViewMode}
          rightActions={rightActions}
          updateQuery={(value: string) => {
            if (withUpdateUrl) {
              updateQuery({
                search: value,
              });
            }
          }}
          customizableColumns={
            customizableColumns
              ? ({
                  ...customizableColumns,
                  onClick: toggleOrderModal,
                } as DataTableProps["customizableColumns"] & {
                  onClick: () => void;
                })
              : undefined
          }
        />
      )}
      {tabFilters && (
        <TabFilters
          {...tabFilters}
          updateQuery={(value: string[]) => {
            if (withUpdateUrl && tabFilters.propertyToFilterBy) {
              updateQuery({
                [tabFilters?.propertyToFilterBy]: value,
              });
            }
          }}
          resetQuery={() => {
            if (withUpdateUrl && tabFilters.propertyToFilterBy) {
              resetQuery();
            }
          }}
        />
      )}
      {filters && <FiltersMenu filters={filters} />}
      {memoizedTable}
      {renderServerPaginationControls()}
      {isOrderModalOpen && (
        <ColumnsOrderModal
          toggleVisibility={toggleOrderModal}
          columns={enhancedColumns.filter(c => c.orderable !== false)}
          defaultColumns={columns}
          updateColumns={updateColumnsOrder}
        />
      )}
    </Layout>
  );
};

export default DataTable;
